import { useEffect, useRef, useState, type AnchorHTMLAttributes, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Settings2, TriangleAlert } from 'lucide-react';
import { alternarDiscreto, conferirInatividade, definirDiscreto, definirPrefsDiscreto, discretoAtivo, marcarAtividade, OPCOES_INATIVIDADE, type MinutosInatividade } from '../nucleo/discreto';
import { Alternador, Selecao } from './formulario';
import { Confirmar, Modal } from './sobreposicao';
import { useDiscreto } from './sensivel';
import { iniciarAnunciadorOculto } from './anunciador-oculto';

/**
 * Atalho do modo discreto: Alt+Shift+D (no Mac, Option+Shift+D). Não colide com o navegador (Chrome, Firefox, Safari e Edge
 * não usam), com os leitores de tela (NVDA usa Insert, VoiceOver usa Control+Option) nem com os atalhos do app (F8 e as
 * teclas dos componentes). Lido pelo `code` da tecla, então vale em qualquer layout de teclado.
 */
export const ATALHO_DISCRETO = 'Alt+Shift+D';
export const ehAtalhoDiscreto = (e: Pick<KeyboardEvent, 'code' | 'altKey' | 'shiftKey' | 'ctrlKey' | 'metaKey'>) => e.code === 'KeyD' && e.altKey && e.shiftKey && !e.ctrlKey && !e.metaKey;

/** Botão de olho: liga e desliga o modo discreto. `aria-pressed` = valores ocultos. */
export function BotaoDiscreto({ tamanho = 'size-4' }: { tamanho?: string }) {
  const { t } = useTranslation();
  const { ativo } = useDiscreto();
  const acao = ativo ? t('discreto.mostrar') : t('discreto.ocultar');
  return (
    <button
      type="button"
      data-discreto-botao
      // Alternância (como o tema): nome fixo "Ocultar valores" e o estado em aria-pressed; o tooltip diz a ação do próximo clique.
      aria-label={t('discreto.ocultar')}
      aria-pressed={ativo}
      aria-keyshortcuts={ATALHO_DISCRETO}
      title={t('discreto.dica', { acao, atalho: ATALHO_DISCRETO })}
      onClick={() => alternarDiscreto()}
      // Ligado com borda de 3:1 além do ícone: o estado não depende só da forma (r6 visual B1/B9).
      className={`rounded-md border-2 p-3 text-tinta-2 lg:p-1.5 hover:bg-sup-2 hover:text-tinta-1 ${ativo ? 'border-tinta-3' : 'border-transparent'}`}
    >
      {ativo ? <EyeOff className={tamanho} aria-hidden /> : <Eye className={tamanho} aria-hidden />}
    </button>
  );
}

/** Preferências do modo discreto (guardadas no aparelho): ligar sozinho por inatividade e censurar nomes. */
export function PreferenciasDiscreto({ tamanho = 'size-4' }: { tamanho?: string }) {
  const { t } = useTranslation();
  const [aberto, setAberto] = useState(false);
  return (
    <>
      <button
        type="button"
        data-discreto-prefs
        aria-label={t('discreto.preferencias')}
        title={t('discreto.preferencias')}
        aria-haspopup="dialog"
        onClick={() => setAberto(true)}
        className="rounded-md border-2 border-transparent p-3 text-tinta-2 lg:p-1.5 hover:bg-sup-2 hover:text-tinta-1"
      >
        <Settings2 className={tamanho} aria-hidden />
      </button>
      <Modal aberto={aberto} aoFechar={() => setAberto(false)} titulo={t('discreto.preferencias')}>
        <FormPreferenciasDiscreto prefixo="discreto" />
      </Modal>
    </>
  );
}

/**
 * Campos das preferências do modo discreto: no diálogo ao lado do olho e em Minha conta (r9 spec B5). `prefixo` separa os
 * ids quando os dois aparecem na mesma tela. `comModo` acrescenta o alternador do próprio modo (Minha conta).
 */
export function FormPreferenciasDiscreto({ prefixo, comModo }: { prefixo: string; comModo?: boolean }) {
  const { t } = useTranslation();
  const { ativo, prefs } = useDiscreto();
  return (
    <div className="flex flex-col gap-5 text-sm">
      {comModo && (
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-medium text-tinta-1">{t('discreto.ocultar')}</p>
            <p className="text-xs text-tinta-3">{t('discreto.secaoAjuda')}</p>
          </div>
          <Alternador id={`${prefixo}-modo`} rotulo={t('discreto.ocultar')} marcado={ativo} aoMudar={(v) => definirDiscreto(v)} />
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${prefixo}-inatividade`} className="font-medium text-tinta-1">
          {t('discreto.inatividade')}
        </label>
        <Selecao
          id={`${prefixo}-inatividade`}
          aria-describedby={`${prefixo}-inatividade-ajuda`}
          value={String(prefs.inatividade)}
          onChange={(e) => definirPrefsDiscreto({ inatividade: Number(e.target.value) as MinutosInatividade })}
          opcoes={OPCOES_INATIVIDADE.map((m) => ({ valor: String(m), rotulo: m === 0 ? t('discreto.inatividadeDesligado') : t('discreto.minutos', { count: m }) }))}
        />
        <p id={`${prefixo}-inatividade-ajuda`} className="text-xs text-tinta-3">
          {t('discreto.inatividadeAjuda')}
        </p>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-medium text-tinta-1">{t('discreto.nomes')}</p>
          <p className="text-xs text-tinta-3">{t('discreto.nomesAjuda')}</p>
        </div>
        <Alternador id={`${prefixo}-nomes`} rotulo={t('discreto.nomes')} marcado={prefs.nomes} aoMudar={(v) => definirPrefsDiscreto({ nomes: v })} />
      </div>
      <p className="text-xs text-tinta-3">{t('discreto.atalho', { atalho: ATALHO_DISCRETO })}</p>
      <p className="text-xs text-tinta-3">{t('discreto.noAparelho')}</p>
    </div>
  );
}

/** Olho e preferências lado a lado, para a barra superior. */
export function ControlesDiscreto({ tamanho }: { tamanho?: string }) {
  return (
    <div className="flex shrink-0 items-center gap-1" data-sem-impressao>
      <BotaoDiscreto tamanho={tamanho} />
      <PreferenciasDiscreto tamanho={tamanho} />
    </div>
  );
}

/** Modal das preferências do modo discreto (controlado por quem abre: o menu do perfil da casca nova). */
export function ModalPreferenciasDiscreto({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation();
  return (
    <Modal aberto={aberto} aoFechar={aoFechar} titulo={t('discreto.preferencias')}>
      <FormPreferenciasDiscreto prefixo="discreto" />
    </Modal>
  );
}

const EVENTOS_ATIVIDADE = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

/**
 * Review r59 robustez M1: com o foco no quadro de outra origem (o cofre embutido), as teclas, o ponteiro e a rolagem vão ao
 * documento do destino, que este vigia não escuta. Foco no quadro com a aba visível conta como atividade.
 *
 * Review r60 (Segurança, Robustez, Spec e Visual M1): só com a janela no foco do sistema (`document.hasFocus()`, verdadeiro
 * quando o foco está num quadro filho da janela em foco): com outro programa ou outra janela à frente, as teclas não vão ao
 * cofre. E esse crédito tem teto (`TETO_QUADRO_MS`, ver `creditoDoQuadro`), porque a atividade vale para o aparelho inteiro.
 */
export function focoNoQuadroExterno(): boolean {
  if (typeof document === 'undefined' || document.visibilityState === 'hidden') return false;
  if (!document.hasFocus()) return false;
  const el = document.activeElement;
  return !!el && el.tagName === 'IFRAME' && el.hasAttribute('data-quadro-externo');
}

/**
 * Review r60 M1 e r61: teto fixo do crédito do foco parado no quadro, contado do último sinal real (fixo em 15 min, a opção
 * mais longa da inatividade: com o tempo escolhido, 1 min, quem digita no cofre veria o discreto ligar, o defeito da r59).
 * Passado o teto, a inatividade volta a contar e o discreto liga como em qualquer tela (e tira o foco do quadro, `TelaExterna`).
 *
 * Sinal real (r61, modelo único; nada que o destino embutido faça sozinho conta):
 * (a) evento de entrada no documento do MalgoOS (`EVENTOS_ATIVIDADE`);
 * (b) a aba ou a janela voltar a ser usada (aba visível e `document.hasFocus()` passando de falso a verdadeiro, no
 *     `visibilitychange`, no `focus` da janela ou no laço de 5 s) com o foco no quadro: Ctrl+Tab, clique na aba, Alt+Tab
 *     (r61 ROB-M1 = SPEC-M1 = VIS-M1);
 * (c) ativação da pessoa (`navigator.userActivation.isActive`, que a digitação ou o clique no quadro filho dão também ao pai)
 *     com o foco no quadro, vista no laço de 5 s;
 * (d) a entrada do foco no quadro (o `blur` da janela) só com ativação da pessoa, onde a API existe: o destino consegue gerar
 *     esse `blur` sozinho com `parent.focus()` e `campo.focus()` (r61 SEG-B1). Sem a API, o `blur` conta como antes.
 */
export const TETO_QUADRO_MS = 15 * 60_000;
let sinalReal = Date.now();
/** Sinal real de atividade (ver acima). */
export function marcarSinalReal(agora = Date.now()) {
  sinalReal = Math.max(sinalReal, agora);
}
/** Ativação da pessoa (gesto real) agora; `undefined` sem a API `navigator.userActivation`. */
export function ativacaoDaPessoa(): boolean | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const ua = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation;
  return ua ? ua.isActive : undefined;
}
/** Janela em uso pelo sistema: aba visível e janela com o foco do sistema. */
const janelaEmUso = () => typeof document !== 'undefined' && document.visibilityState !== 'hidden' && document.hasFocus();
/** O foco no quadro vale como atividade agora: janela em foco, aba visível e dentro do teto desde o último sinal real. */
export function creditoDoQuadro(agora = Date.now()): boolean {
  return focoNoQuadroExterno() && agora - sinalReal < TETO_QUADRO_MS;
}

/**
 * Vigia do modo discreto (uma vez no Shell): atalho de teclado, ativação automática por inatividade e o anúncio da troca
 * para o leitor de tela.
 */
export function VigiaDiscreto() {
  const { t } = useTranslation();
  const { ativo } = useDiscreto();
  const [anuncio, setAnuncio] = useState('');
  const primeiro = useRef(true);

  // Leitor de tela: todo texto com a marca de oculto é anunciado como "valor oculto" (r9 visual B2).
  useEffect(() => iniciarAnunciadorOculto(), []);

  useEffect(() => {
    const atalho = (e: KeyboardEvent) => {
      if (!ehAtalhoDiscreto(e)) return;
      e.preventDefault();
      if (!e.repeat) alternarDiscreto();
    };
    document.addEventListener('keydown', atalho);
    return () => document.removeEventListener('keydown', atalho);
  }, []);

  useEffect(() => {
    // A atividade vale para o aparelho (r9 spec M2): gravada em `localStorage` com throttle e lida por todas as abas.
    sinalReal = Date.now();
    const marcar = () => {
      marcarSinalReal();
      marcarAtividade();
    };
    for (const ev of EVENTOS_ATIVIDADE) window.addEventListener(ev, marcar, { passive: true, capture: true });
    // Conferência a cada 5 s (barata) em vez de um timer reiniciado a cada movimento do mouse; ao voltar para a aba, já.
    let emUso = janelaEmUso();
    const conferir = () => {
      const agoraEmUso = janelaEmUso();
      const voltou = agoraEmUso && !emUso;
      emUso = agoraEmUso;
      // Sinais reais (b) e (c) com o foco no quadro: a volta à aba ou à janela, e a ativação da pessoa.
      if (focoNoQuadroExterno() && (voltou || ativacaoDaPessoa() === true)) marcarSinalReal();
      if (creditoDoQuadro()) marcarAtividade();
      conferirInatividade();
    };
    const id = window.setInterval(conferir, 5_000);
    document.addEventListener('visibilitychange', conferir);
    window.addEventListener('focus', conferir);
    // Clique ou Tab para dentro do quadro: a janela perde o foco e o `activeElement` passa a ser o iframe (já, ou na volta do laço).
    let espera = 0;
    // A entrada do foco no quadro é sinal real (d) só com gesto da pessoa (r61 SEG-B1); sem ele, só o crédito dentro do teto.
    const entrou = () => {
      if (!focoNoQuadroExterno()) return false;
      if (ativacaoDaPessoa() !== false) marcarSinalReal();
      if (creditoDoQuadro()) marcarAtividade();
      return true;
    };
    const saiu = () => {
      entrou();
      window.clearTimeout(espera);
      espera = window.setTimeout(entrou, 0);
    };
    window.addEventListener('blur', saiu);
    return () => {
      for (const ev of EVENTOS_ATIVIDADE) window.removeEventListener(ev, marcar, { capture: true });
      window.clearInterval(id);
      window.clearTimeout(espera);
      document.removeEventListener('visibilitychange', conferir);
      window.removeEventListener('focus', conferir);
      window.removeEventListener('blur', saiu);
    };
  }, []);

  useEffect(() => {
    if (primeiro.current) {
      primeiro.current = false;
      return;
    }
    setAnuncio(ativo ? t('discreto.ligado') : t('discreto.desligado'));
  }, [ativo, t]);

  return (
    <p role="status" className="sr-only" data-livre-do-trap>
      {anuncio}
    </p>
  );
}

/** Aviso dentro de uma tela de exportação: com o modo discreto ligado, o arquivo gerado pelo servidor sai com os valores reais. */
export function AvisoExportacao() {
  const { t } = useTranslation();
  const { ativo } = useDiscreto();
  if (!ativo) return null;
  return (
    <p role="note" data-aviso-exportacao className="mb-4 flex items-start gap-2 rounded-lg border border-laranja px-3 py-2 text-sm text-tinta-1">
      <TriangleAlert className="mt-0.5 size-4 shrink-0 text-laranja" aria-hidden />
      <span>{t('discreto.exportarAviso')}</span>
    </p>
  );
}

/**
 * Link de arquivo gerado pelo servidor (PDF, planilha): com o modo discreto ligado, pede confirmação antes de baixar, porque o
 * arquivo sai com os valores reais.
 */
export function LinkExportacao({ href, onClick, children, ...resto }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const { t } = useTranslation();
  const [confirmar, setConfirmar] = useState(false);
  const ref = useRef<HTMLAnchorElement>(null);
  const liberado = useRef(false);
  return (
    <>
      <a
        ref={ref}
        href={href}
        {...resto}
        onClick={(e: MouseEvent<HTMLAnchorElement>) => {
          onClick?.(e);
          if (liberado.current) {
            liberado.current = false;
            return;
          }
          if (discretoAtivo()) {
            e.preventDefault();
            setConfirmar(true);
          }
        }}
      >
        {children}
      </a>
      {/* O diálogo vai num portal, mas o clique sobe pela árvore do React: não deixa chegar ao clique da linha da tabela. */}
      <span className="contents" onClick={(e) => e.stopPropagation()}>
        <Confirmar
          aberto={confirmar}
          aoFechar={() => setConfirmar(false)}
          titulo={t('discreto.exportarTitulo')}
          texto={<p data-aviso-exportacao>{t('discreto.exportarAviso')}</p>}
          aoConfirmar={() => {
            setConfirmar(false);
            liberado.current = true;
            ref.current?.click();
          }}
        />
      </span>
    </>
  );
}
